import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/FAQ.css";

/**
 * Public FAQ, linked from Help & Support on both platforms.
 *
 * Deliberately operational rather than clinical: how the service works, not
 * medical guidance. Prices are not listed — they come from the database via
 * /subscription-plans so the advertised and billed price cannot drift apart, so
 * this points at the pricing page instead of restating numbers.
 */
const FAQS = [
  {
    q: "What is Youro?",
    a: "Youro is a telehealth service for urological care. You complete a symptom assessment, meet a urology provider by video, and receive a care plan you can follow between visits.",
  },
  {
    q: "How do I book an appointment?",
    a: "Sign in and open Appointments. Pick a provider and a time that suits you from their available slots, and you will get a confirmation with a link to join.",
  },
  {
    q: "What is the symptom assessment?",
    a: "A short questionnaire about your symptoms. It produces a score your provider uses to understand how much your symptoms are affecting you, and to track whether treatment is helping over time.",
  },
  {
    q: "How do I read my care plan?",
    a: "Your provider publishes it after your appointment and you will get an email when it is ready. You can read it any time from your home page.",
  },
  {
    q: "How do prescriptions work?",
    a: "Any prescriptions your provider issues appear in your Prescriptions tab. Take them to the pharmacy, lab or imaging centre of your choice — Youro does not dispense medication.",
  },
  {
    q: "How do I request a refill?",
    a: "Open the prescription in your Prescriptions tab and request a refill from there. Your provider reviews each request and you will be notified once they have responded.",
  },
  {
    q: "What does membership cost?",
    a: "Current plans and prices are listed on the Pricing page, which always shows what you would actually be billed.",
  },
  {
    q: "What's the difference between monthly and annual?",
    a: "Annual costs less overall and is billed once a year. Monthly is billed each month with a 3-month minimum, then you can cancel anytime.",
  },
  {
    q: "Does my membership include medications?",
    a: "No. Your provider prescribes, and you fill the prescription through your own insurance or pharmacy. Labs work the same way.",
  },
  {
    q: "I started with On Demand. Can I upgrade?",
    a: "Yes. Choose a membership within 30 days of buying On Demand and what you paid for it counts toward your membership.",
  },
  {
    q: "Can I cancel my membership?",
    a: "Yes, from the Billing section of your profile. Cancelling stops future renewals. A monthly membership cancelled during its 3-month minimum carries on, and is billed monthly, until the minimum ends. Cancelling does not automatically refund what you have already paid, so contact support if you think a refund is due.",
  },
  {
    q: "How do I message my provider?",
    a: "Use the Chat tab once you have an appointment with them. Messaging is for quick questions about your treatment plan, such as refills, side effects and clarifications. Your provider replies within 1–2 business days. If a question needs a fuller evaluation, we'll ask you to book a visit. Messaging is not for emergencies: if you have severe pain, fever with urinary symptoms, inability to urinate, or blood clots in your urine, call 911 or go to the nearest emergency room.",
  },
  {
    q: "Is my information private?",
    a: "Your records are only visible to you and the care team treating you. See the Privacy Policy for how your information is stored and used.",
  },
  {
    q: "What if I have a medical emergency?",
    a: "Call 911 or go to your nearest emergency room. Youro is for non-urgent care and is not monitored around the clock.",
  },
  {
    q: "How do I contact support?",
    a: "Email support@yourohealth.com, or use the message form on the Help & Support page. Support hours are Monday to Friday 8am-8pm, Saturday and Sunday 10am-6pm EST.",
  },
];

const FAQ = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="faq-page">
      <main className="faq-main">
        <button className="faq-back" type="button" onClick={() => navigate(-1)}>
          <span aria-hidden="true">←</span> Back
        </button>

        <h1 className="faq-title">Frequently Asked Questions</h1>
        <p className="faq-subtitle">
          Answers to the questions we are asked most. If yours is not here,
          contact support and we will help.
        </p>

        <ul className="faq-list">
          {FAQS.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <li className="faq-item" key={item.q}>
                <button
                  type="button"
                  className="faq-question"
                  aria-expanded={isOpen}
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                >
                  <span className={"faq-chevron" + (isOpen ? " faq-chevron-open" : "")}>
                    ›
                  </span>
                  {item.q}
                </button>
                {isOpen && <p className="faq-answer">{item.a}</p>}
              </li>
            );
          })}
        </ul>

        <section className="faq-emergency" aria-label="Medical emergency">
          <h2 className="faq-emergency-title">Medical Emergency?</h2>
          <p>
            If you are experiencing a medical emergency, call 911 immediately or
            go to your nearest emergency room. Youro support is for non-urgent
            questions only.
          </p>
        </section>
      </main>
    </div>
  );
};

export default FAQ;
